// ----------------------------------------------------------------------
// <copyright file="Izenda.SchedulingSearchStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

!(function () {

  var extendObservable = require('mobx').extendObservable;
  var uiState = require('logic/Izenda.UiState');
  var SchedulingSearchCriteria = require('./models/Izenda.SchedulingSearchCriteria');
  var SchedulingSearchResult = require('./models/Izenda.SchedulingSearchResult');
  var PagingModel = require('share/components/controls/DataGrid/helpers/Izenda.PagingModel');
  var systemFetcher = require('fetchers/Izenda.SystemSettingFetcher');
  var CONSTANTS = require('logic/data/Izenda.SchedulingSearchConstants');
  var PAGE_SEARCH_SIZE = CONSTANTS.PAGE_SEARCH_SIZE;
  var SEARCH_KEY = CONSTANTS.SEARCH_KEY;
  var DEFAULT_SORT_ORDER = CONSTANTS.DEFAULT_SORT_ORDER;
  var REPORT_TYPE_VALUE = CONSTANTS.REPORT_TYPE_VALUE;

  var dashboardFetcher = require('fetchers/Izenda.DashboardDataFetcher');
  var reportFetcher = require('fetchers/Izenda.ReportsDataFetcher');
  
  function SchedulingSearchStore() {
    
    extendObservable(this, {
      criteria: new SchedulingSearchCriteria(),
      pageInfo: new PagingModel(DEFAULT_SORT_ORDER),
      searchResult: new SchedulingSearchResult(),
      emailTemplates: null
    });
  }

  /**
   * Load Email Template
   * @returns {*}
   */
  SchedulingSearchStore.prototype.loadEmailTemplates = function () {
    return dashboardFetcher.fetchEmailTemplates(CONSTANTS.TEMPLATE_TYPE).then(function (result) {
      this.emailTemplates = result;
      return result;
    }.bind(this));
  };

  /**
   * Load Schedules
   * @return {Promise}
   */
  SchedulingSearchStore.prototype.loadSchedules = function () {
    var criteria = this.buildCriteriaData();
    var params = {
      systemLevel: !uiState.tenantId,
      tenantId: uiState.tenantId,
      pageIndex: 1,
      pageSize: PAGE_SEARCH_SIZE,
      sortOrders: this.pageInfo.buildSortOrder(),
      criteria: criteria
    };
    return systemFetcher.loadSchedules(params).then(function (result) {
      this.searchResult.populateData(result);
      return result;
    }.bind(this));
  };

  /**
   * Update schedules on one grid
   * @param data
   */
  SchedulingSearchStore.prototype.updateGridSchedules = function (data) {
    var criteria = this.buildCriteriaData();
    var pageInfo = data.pageInfo;
    var params = {
      systemLevel: false,
      tenantId: !!data.tenantId ? data.tenantId : null,
      pageIndex: pageInfo.pageIndex,
      pageSize: pageInfo.pageSize,
      sortOrders: pageInfo.buildSortOrder(),
      criteria: criteria
    };
    return systemFetcher.loadSchedules(params).then(function (result) {
      this.searchResult.populateData(result, data.pageInfo);
      return result;
    }.bind(this));
  };

  /**
   * Load report dashbord name
   * @param tenantId
   * @returns {*}
   */
  SchedulingSearchStore.prototype.loadReportDashboardName = function (tenantId) {
    var reportingType = this.criteria.reportType;
    reportingType =  REPORT_TYPE_VALUE[reportingType];
    tenantId = tenantId || uiState.tenantId;
    return systemFetcher.getReportDashboardNameByTenant(reportingType || 0, tenantId).bind(this).then(function (result) {
      this.criteria.setValueByKey('reportDashboardNameOptions', result.map(function (item) {
        return {
          value: item,
          label: item
        }
      }));
    });
  };

  /**
   * Load creator by tenant
   * @param tenantId
   * @returns {*}
   */
  SchedulingSearchStore.prototype.loadCreatorByTenant = function (tenantId) {
    tenantId = tenantId || uiState.tenantId;
    return systemFetcher.getCreatedByTenant(tenantId).then(function (result) {
      this.criteria.setOptionByKey('createdByOptions', result);
    }.bind(this));
  };

  /**
   * Load recipients by tenant
   * @param tenantId
   * @returns {*}
   */
  SchedulingSearchStore.prototype.loadRecipientsByTenant = function (tenantId) {
    tenantId = tenantId || uiState.tenantId;
    return systemFetcher.getRecipientByTenant(tenantId).then(function (result) {
      this.criteria.setOptionByKey('recipientsOptions', result);
    }.bind(this));
  };

  /**
   * Build search criteria array
   * @returns {Array}
   */
  SchedulingSearchStore.prototype.buildCriteriaData = function () {
    var criteria = this.criteria.buildDataForSearch();
    var criteriaKeys = Object.keys(SEARCH_KEY);
    return criteriaKeys.map(function (key) {
        return {
          key: SEARCH_KEY[key],
          value: criteria[key]
        }
      })
  };

  /**
   * Save Schedules
   * @param {object} subscription
   * @returns {promise}
   */
  SchedulingSearchStore.prototype.saveSchedule = function (subscription) {
    var postData;
    var saveFunction;
    if (subscription.dashboardId) {
      postData = {
        id: subscription.dashboardId,
        subscriptions: [subscription]
      };
      saveFunction = dashboardFetcher.saveSubscriptions;
    } else {
      postData = [subscription];
      saveFunction = reportFetcher.updateSubscriptions;
    }
    return saveFunction(postData);
  };

  /**
   * Validate Schedule
   * @param {object} schedule
   * @param {object} commonFilterFields
   * @returns {*|Promise}
   */
  SchedulingSearchStore.prototype.validateSchedule = function (schedule, commonFilterFields) {
    var fetcher = schedule.dashboardId ? dashboardFetcher : reportFetcher;
    return fetcher.validateSubscription(schedule.omitData(), commonFilterFields);
  };

  /**
   * Delete one or many schedules
   * @param {array} data
   */
  SchedulingSearchStore.prototype.deleteSchedules = function (data) {
    return systemFetcher.deleteSchedules(data);
  };

  /**
   * Reset Search result data
   */
  SchedulingSearchStore.prototype.resetData = function () {
    this.searchResult = new SchedulingSearchResult();
    this.pageInfo = new PagingModel(DEFAULT_SORT_ORDER);
    this.criteria.resetData();
  };
  
  module.exports = SchedulingSearchStore;
  
}());


// WEBPACK FOOTER //
// ./ui/logic/Izenda.SchedulingSearchStore.js